import React, {useState, useEffect} from 'react';
import styles from '../tables.module.css';
import {PRISMAX_BACKEND_URL} from "../../../configs/config";

const AdminWhitelistTable = () => {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [walletAddress, setWalletAddress] = useState('');
    const [confirmWalletAddress, setConfirmWalletAddress] = useState('');
    const [addError, setAddError] = useState('');

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const itemsPerPage = 20;

    // Fetch admin whitelist data
    const fetchAdmins = async () => {
        try {
            const token = localStorage.getItem("prismax_admin_token");
            const headers = token ? {"Authorization": `Bearer ${token}`} : {};
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/get-admin-whitelist`, {headers});
            if (!response.ok) {
                throw new Error('Failed to fetch admin whitelist');
            }
            const data = await response.json();
            if (data.success && data.data) {
                setAdmins(data.data);
                setTotalPages(Math.ceil(data.data.length / itemsPerPage));
            } else {
                throw new Error('Invalid data format');
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAdmins();
    }, []);

    // Get current page items
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = admins.slice(indexOfFirstItem, indexOfLastItem);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Go to previous page
    const goToPreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    // Go to next page
    const goToNextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    // Add new admin
    const handleAddAdmin = async () => {
        setAddError('');

        // Validate inputs
        if (!walletAddress) {
            setAddError('Please fill in the  wallet address fields');
            return;
        }


        try {
            const token = localStorage.getItem("prismax_admin_token");

            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/add-admin-whitelist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,

                },
                body: JSON.stringify({
                    wallet_address: walletAddress
                })
            });

            const data = await response.json();

            if (data.success) {
                // Reset form
                setWalletAddress('');
                setConfirmWalletAddress('');
                setShowAddModal(false);
                // Refresh data
                fetchAdmins();
            } else {
                setAddError(data.msg || 'Failed to add admin');
            }
        } catch (err) {
            setAddError('Failed to add admin: ' + err.message);
        }
    };

    // Delete admin
    const handleDeleteAdmin = async (adminId) => {
        if (!window.confirm('Are you sure you want to delete this admin?')) {
            return;
        }

        try {
            const token = localStorage.getItem("prismax_admin_token");
            const response = await fetch(`${PRISMAX_BACKEND_URL}/api/delete-admin-whitelist`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({
                    admin_id: adminId
                })
            });

            const data = await response.json();

            if (data.success) {
                // Refresh data
                fetchAdmins();
            } else {
                alert(data.msg || 'Failed to delete admin');
            }
        } catch (err) {
            alert('Failed to delete admin: ' + err.message);
        }
    };

    if (loading) {
        return <div className={styles.loading}>Loading admin whitelist...</div>;
    }

    if (error) {
        return <div className={styles.error}>Error: {error}</div>;
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.tableHeader}>
                <h2 className={styles.tableTitle}>Admin Whitelist</h2>
                <button
                    className={styles.addButton}
                    onClick={() => setShowAddModal(true)}
                >
                    Add Admin
                </button>
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                    <tr>
                        <th>Admin ID</th>
                        <th>Wallet Address</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentItems.length > 0 ? (
                        currentItems.map((admin) => (
                            <tr key={admin.admin_id}>
                                <td>{admin.admin_id}</td>
                                <td>{admin.wallet_address}</td>
                                <td>
                                    <button
                                        className={styles.deleteButton}
                                        onClick={() => handleDeleteAdmin(admin.admin_id)}
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="3" className={styles.noData}>
                                No admins found
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {admins.length > itemsPerPage && (
                <div className={styles.pagination}>
                    <button
                        onClick={goToPreviousPage}
                        disabled={currentPage === 1}
                        className={`${styles.pageButton} ${currentPage === 1 ? styles.disabled : ''}`}
                    >
                        Previous
                    </button>

                    <div className={styles.pageNumbers}>
                        {Array.from({length: totalPages}, (_, i) => i + 1).map((number) => {
                            if (
                                number === 1 ||
                                number === totalPages ||
                                (number >= currentPage - 2 && number <= currentPage + 2)
                            ) {
                                return (
                                    <button
                                        key={number}
                                        onClick={() => paginate(number)}
                                        className={`${styles.pageNumber} ${currentPage === number ? styles.active : ''}`}
                                    >
                                        {number}
                                    </button>
                                );
                            } else if (
                                (number === currentPage - 3 && currentPage > 4) ||
                                (number === currentPage + 3 && currentPage < totalPages - 3)
                            ) {
                                return <span key={number} className={styles.ellipsis}>...</span>;
                            }
                            return null;
                        })}
                    </div>

                    <button
                        onClick={goToNextPage}
                        disabled={currentPage === totalPages}
                        className={`${styles.pageButton} ${currentPage === totalPages ? styles.disabled : ''}`}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Add Admin Modal */}
            {showAddModal && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modal}>
                        <h3>Add New Admin</h3>

                        <div className={styles.formGroup}>
                            <label>Wallet Address:</label>
                            <input
                                type="text"
                                value={walletAddress}
                                onChange={(e) => setWalletAddress(e.target.value)}
                                placeholder="Enter wallet address"
                                className={styles.input}
                            />
                        </div>


                        {addError && (
                            <div className={styles.errorMessage}>{addError}</div>
                        )}

                        <div className={styles.modalButtons}>
                            <button
                                className={styles.cancelButton}
                                onClick={() => {
                                    setShowAddModal(false);
                                    setWalletAddress('');
                                    setAddError('');
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.confirmButton}
                                onClick={handleAddAdmin}
                            >
                                Add Admin
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminWhitelistTable;
